"use client"

import { useMemo } from "react"
import { formatDistanceToNowStrict } from "date-fns"

import { asArray } from "@/lib/as-array"
import { useGetSearch } from "openapi/api/endpoints"

/**
 * The searches you have run before, for canvas 19e's "Searches like this".
 *
 * The artboard says "your 20 most recent, by last used". A search row is
 * created when it is run and never touched again, so `created_at` is when it
 * was last *run*, not when it was last opened — the line says "run" rather than
 * "used" so it is not claiming to know something it does not.
 */

export type RecentSearch = {
  hash: string
  prompt: string
  results?: number
  when: string
  at: number
}

export function useRecentSearches(limit = 20) {
  const { data, isLoading } = useGetSearch()

  const searches = useMemo<RecentSearch[]>(() => {
    return asArray<Record<string, unknown>>(data)
      .map((row) => {
        const at = new Date((row.created_at as string) ?? 0).getTime()
        return {
          hash: (row.normalized_query_hash as string) ?? "",
          prompt: ((row.prompt as string) ?? "").trim(),
          results: row.total_result_count as number | undefined,
          when: Number.isFinite(at) && at > 0
            ? `run ${formatDistanceToNowStrict(at, { addSuffix: true })}`
            : "run recently",
          at: Number.isFinite(at) ? at : 0,
        }
      })
      .filter((search) => search.hash && search.prompt)
      .sort((a, b) => b.at - a.at)
      .slice(0, limit)
  }, [data, limit])

  return { searches, isLoading }
}
